import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Fab,
  Badge,
  Paper,
  Typography,
  TextField,
  Button,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Autocomplete,
} from '@mui/material';
import ChatIcon from '@mui/icons-material/Chat';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import EditIcon from '@mui/icons-material/Edit';
import { messengerService, staffService } from '../services/api';

const SK_OPEN = 'messenger.open';
const SK_CONV = 'messenger.convId';

function normList(res) {
  const d = res.data;
  return Array.isArray(d?.results) ? d.results : Array.isArray(d) ? d : [];
}

function conversationLabel(c, myId) {
  if ((c.title || '').trim()) return c.title.trim();
  const parts = (c.participants || [])
    .filter((p) => Number(p.staff) !== Number(myId))
    .map((p) => p.staff_name || String(p.staff));
  return parts.length ? parts.join(', ') : `Діалог #${c.id}`;
}

export default function MessengerWidget({ currentUser }) {
  const myId = currentUser?.id;

  const [open, setOpen] = useState(() => sessionStorage.getItem(SK_OPEN) === '1');
  const [activeId, setActiveId] = useState(() => {
    const r = sessionStorage.getItem(SK_CONV);
    const n = r ? parseInt(r, 10) : NaN;
    return Number.isFinite(n) ? n : null;
  });
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [unreadTotal, setUnreadTotal] = useState(0);
  const [composeOpen, setComposeOpen] = useState(false);
  const [renameOpen, setRenameOpen] = useState(false);
  const [renameTitle, setRenameTitle] = useState('');
  const [staffOptions, setStaffOptions] = useState([]);
  const [selectedStaff, setSelectedStaff] = useState([]);
  const [newTitle, setNewTitle] = useState('');
  const [inputText, setInputText] = useState('');
  const [savingConv, setSavingConv] = useState(false);

  const unreadPollRef = useRef(null);
  const msgPollRef = useRef(null);

  const persistOpen = (v) => {
    setOpen(v);
    sessionStorage.setItem(SK_OPEN, v ? '1' : '0');
  };

  const persistConv = (id) => {
    setActiveId(id);
    if (id != null) sessionStorage.setItem(SK_CONV, String(id));
    else sessionStorage.removeItem(SK_CONV);
  };

  const refreshUnread = useCallback(async () => {
    if (!myId) {
      setUnreadTotal(0);
      return;
    }
    try {
      const r = await messengerService.unreadCount();
      setUnreadTotal(Number(r.data?.count) || 0);
    } catch {
      setUnreadTotal(0);
    }
  }, [myId]);

  const loadConversations = useCallback(async () => {
    if (!myId) return;
    try {
      const r = await messengerService.listConversations();
      setConversations(normList(r));
    } catch (e) {
      console.error(e);
    }
  }, [myId]);

  const fetchMessagesSilently = useCallback(async (convId) => {
    if (!convId) return;
    try {
      const r = await messengerService.listMessages(convId);
      const list = Array.isArray(r.data) ? r.data : [];
      setMessages((prev) => {
        if (list.length !== prev.length) return list;
        const a = list[list.length - 1];
        const b = prev[prev.length - 1];
        if (!a || !b) return list;
        return a.id === b.id ? prev : list;
      });
    } catch {
      /* ignore */
    }
  }, []);

  const openConversation = async (convId) => {
    persistConv(convId);
    try {
      const r = await messengerService.listMessages(convId);
      setMessages(Array.isArray(r.data) ? r.data : []);
      await messengerService.markRead(convId);
      await refreshUnread();
      const r2 = await messengerService.listConversations();
      setConversations(normList(r2));
    } catch (e) {
      console.error(e);
    }
  };

  useEffect(() => {
    if (!myId) return undefined;
    refreshUnread();
    loadConversations();
    unreadPollRef.current = setInterval(() => {
      refreshUnread();
      if (sessionStorage.getItem(SK_OPEN) === '1') {
        loadConversations();
      }
    }, 4000);
    return () => {
      if (unreadPollRef.current) clearInterval(unreadPollRef.current);
    };
  }, [myId, refreshUnread, loadConversations]);

  // При відкритті діалогу (у т.ч. відновленого з session) — одразу mark-read, щоб лічильник зникав
  useEffect(() => {
    if (!open || !activeId || !myId) return undefined;
    let cancelled = false;
    (async () => {
      try {
        await messengerService.markRead(activeId);
        if (!cancelled) await refreshUnread();
      } catch {
        /* ignore */
      }
    })();
    return () => { cancelled = true; };
  }, [open, activeId, myId, refreshUnread]);

  useEffect(() => {
    if (!open || !activeId) {
      if (msgPollRef.current) clearInterval(msgPollRef.current);
      return undefined;
    }
    msgPollRef.current = setInterval(() => {
      fetchMessagesSilently(activeId);
      messengerService.markRead(activeId).then(() => refreshUnread()).catch(() => refreshUnread());
    }, 2500);
    return () => {
      if (msgPollRef.current) clearInterval(msgPollRef.current);
    };
  }, [open, activeId, fetchMessagesSilently, refreshUnread]);

  const openCompose = async () => {
    setComposeOpen(true);
    setSelectedStaff([]);
    setNewTitle('');
    try {
      const res = await staffService.getAll({ is_active: true });
      const list = Array.isArray(res.data?.results) ? res.data.results : Array.isArray(res.data) ? res.data : [];
      setStaffOptions(list.filter((s) => Number(s.id) !== Number(myId)));
    } catch (e) {
      console.error(e);
      setStaffOptions([]);
    }
  };

  const createConversation = async () => {
    const ids = selectedStaff.map((s) => s.id);
    if (!ids.length) {
      alert('Оберіть хоча б одного співрозмовника');
      return;
    }
    try {
      setSavingConv(true);
      const r = await messengerService.createConversation({
        title: (newTitle || '').trim(),
        participant_ids: ids,
      });
      const conv = r.data;
      setComposeOpen(false);
      await loadConversations();
      await openConversation(conv.id);
      persistOpen(true);
    } catch (e) {
      console.error(e);
      const msg =
        e.response?.data &&
        (typeof e.response.data === 'string' ? e.response.data : JSON.stringify(e.response.data, null, 2));
      alert(msg || 'Не вдалося створити діалог');
    } finally {
      setSavingConv(false);
    }
  };

  const send = async () => {
    const t = inputText.trim();
    if (!t || !activeId) return;
    setInputText('');
    const tempId = `tmp-${Date.now()}`;
    const optimistic = {
      id: tempId,
      sender: myId,
      sender_name: currentUser?.full_name || currentUser?.username || '',
      body: t,
      created_at: new Date().toISOString(),
    };
    setMessages((m) => [...m, optimistic]);
    try {
      const r = await messengerService.sendMessage(activeId, t);
      const real = r.data;
      setMessages((m) => m.map((x) => (String(x.id) === String(tempId) ? real : x)));
      await refreshUnread();
      loadConversations();
    } catch (e) {
      setMessages((m) => m.filter((x) => String(x.id) !== String(tempId)));
      alert('Не вдалося надіслати повідомлення');
    }
  };

  const removeConversation = async (c) => {
    if (!window.confirm('Видалити цей діалог? Повідомлення будуть втрачені.')) return;
    try {
      await messengerService.deleteConversation(c.id);
      if (activeId === c.id) {
        persistConv(null);
        setMessages([]);
      }
      await loadConversations();
      await refreshUnread();
    } catch (e) {
      console.error(e);
      alert('Видалити може лише той, хто створив діалог');
    }
  };

  const openRename = () => {
    const cur = conversations.find((x) => x.id === activeId);
    if (!cur) return;
    setRenameTitle((cur.title || '').trim());
    setRenameOpen(true);
  };

  const saveRename = async () => {
    if (!activeId) return;
    try {
      const title = (renameTitle || '').trim();
      await messengerService.updateConversation(activeId, { title });
      setRenameOpen(false);
      await loadConversations();
    } catch (e) {
      console.error(e);
      const msg =
        e.response?.data &&
        (typeof e.response.data === 'string' ? e.response.data : JSON.stringify(e.response.data, null, 2));
      alert(msg || 'Не вдалося зберегти назву');
    }
  };

  if (!myId) return null;

  return (
    <>
      <Box
        sx={{
          position: 'fixed',
          right: 24,
          bottom: 24,
          zIndex: (theme) => theme.zIndex.drawer + 3,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 1,
        }}
      >
        {open ? (
          <Paper
            elevation={8}
            sx={{
              width: { xs: 'calc(100vw - 48px)', sm: 420 },
              maxWidth: 440,
              height: 'min(72vh, 560px)',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                px: 1.5,
                py: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                borderBottom: 1,
                borderColor: 'divider',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              <Typography variant="subtitle1" fontWeight={600}>
                Месенджер
              </Typography>
              <IconButton size="small" color="inherit" onClick={() => persistOpen(false)} aria-label="Закрити">
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ display: 'flex', flex: 1, minHeight: 0 }}>
              <Box
                sx={{
                  width: 150,
                  flexShrink: 0,
                  borderRight: 1,
                  borderColor: 'divider',
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Box sx={{ p: 0.5 }}>
                  <Button fullWidth size="small" variant="outlined" color="inherit" onClick={openCompose}>
                    Новий діалог
                  </Button>
                </Box>
                <Divider />
                <List dense sx={{ overflow: 'auto', flex: 1, py: 0 }}>
                  {conversations.map((c) => (
                    <ListItemButton
                      key={c.id}
                      selected={activeId === c.id}
                      onClick={() => openConversation(c.id)}
                      alignItems="flex-start"
                      sx={{ flexDirection: 'column', alignItems: 'stretch', py: 1 }}
                    >
                      <Typography variant="caption" noWrap sx={{ fontWeight: activeId === c.id ? 700 : 500 }}>
                        {conversationLabel(c, myId)}
                        {Number(c.unread_count) > 0 ? (
                          <Typography component="span" variant="caption" color="error.main" sx={{ ml: 0.5 }}>
                            ({c.unread_count})
                          </Typography>
                        ) : null}
                      </Typography>
                    </ListItemButton>
                  ))}
                  {!conversations.length ? (
                    <ListItemText primary="Немає діалогів" primaryTypographyProps={{ variant: 'caption', px: 1 }} />
                  ) : null}
                </List>
              </Box>
              <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
                {activeId ? (
                  <>
                    <Box
                      sx={{
                        px: 1,
                        py: 0.5,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        borderBottom: 1,
                        borderColor: 'divider',
                      }}
                    >
                      <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                        {conversationLabel(
                          conversations.find((x) => x.id === activeId) || { id: activeId },
                          myId,
                        )}
                      </Typography>
                      {(() => {
                        const cur = conversations.find((x) => x.id === activeId);
                        if (!cur || Number(cur.created_by) !== Number(myId)) return null;
                        return (
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton
                              size="small"
                              onClick={openRename}
                              aria-label="Перейменувати діалог"
                              title="Перейменувати"
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => removeConversation(cur)}
                            aria-label="Видалити діалог"
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                          </Box>
                        );
                      })()}
                    </Box>
                    <Box sx={{ flex: 1, overflow: 'auto', p: 1, bgcolor: 'grey.50' }}>
                      {messages.map((m) => {
                        const mine = Number(m.sender) === Number(myId);
                        return (
                          <Box
                            key={m.id}
                            sx={{
                              display: 'flex',
                              justifyContent: mine ? 'flex-end' : 'flex-start',
                              mb: 1,
                            }}
                          >
                            <Paper
                              elevation={0}
                              sx={{
                                px: 1.25,
                                py: 0.75,
                                maxWidth: '88%',
                                bgcolor: mine ? 'primary.light' : 'background.paper',
                                color: mine ? 'primary.contrastText' : 'text.primary',
                              }}
                            >
                              {!mine ? (
                                <Typography variant="caption" display="block" sx={{ opacity: 0.85 }}>
                                  {m.sender_name || '—'}
                                </Typography>
                              ) : null}
                              <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                {m.body}
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 0.25 }}>
                                {m.created_at
                                  ? new Date(m.created_at).toLocaleString('uk-UA', {
                                      day: '2-digit',
                                      month: '2-digit',
                                      hour: '2-digit',
                                      minute: '2-digit',
                                    })
                                  : ''}
                              </Typography>
                            </Paper>
                          </Box>
                        );
                      })}
                    </Box>
                    <Box sx={{ p: 1, display: 'flex', gap: 1, borderTop: 1, borderColor: 'divider' }}>
                      <TextField
                        fullWidth
                        size="small"
                        placeholder="Повідомлення…"
                        value={inputText}
                        onChange={(e) => setInputText(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' && !e.shiftKey) {
                            e.preventDefault();
                            send();
                          }
                        }}
                      />
                      <IconButton color="primary" onClick={send} aria-label="Надіслати">
                        <SendIcon />
                      </IconButton>
                    </Box>
                  </>
                ) : (
                  <Box sx={{ p: 2, flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <Typography color="text.secondary" variant="body2" align="center">
                      Оберіть діалог або створіть новий
                    </Typography>
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        ) : null}

        <Fab
          color="primary"
          aria-label="Месенджер"
          onClick={() => {
            persistOpen(!open);
            if (!open) {
              loadConversations();
              refreshUnread();
            }
          }}
        >
          <Badge color="error" max={99} badgeContent={unreadTotal} invisible={unreadTotal < 1}>
            <ChatIcon />
          </Badge>
        </Fab>
      </Box>

      <Dialog open={composeOpen} onClose={() => setComposeOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Новий діалог</DialogTitle>
        <DialogContent>
          <TextField
            label="Назва (необовʼязково)"
            fullWidth
            margin="normal"
            value={newTitle}
            onChange={(e) => setNewTitle(e.target.value)}
          />
          <Autocomplete
            multiple
            options={staffOptions}
            getOptionLabel={(o) => o.full_name || o.username || String(o.id)}
            value={selectedStaff}
            onChange={(_, v) => setSelectedStaff(v)}
            isOptionEqualToValue={(a, b) => Number(a.id) === Number(b.id)}
            renderInput={(params) => (
              <TextField {...params} label="Учасники" margin="normal" placeholder="Оберіть співробітників" />
            )}
          />
          <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
            Ви будете додані до діалогу автоматично. Можна обрати одного або кількох співрозмовників.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setComposeOpen(false)} disabled={savingConv}>
            Скасувати
          </Button>
          <Button variant="contained" onClick={createConversation} disabled={savingConv}>
            Створити
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={renameOpen} onClose={() => setRenameOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Назва діалогу</DialogTitle>
        <DialogContent sx={{ pt: 2 }}>
          <TextField
            label="Назва (можна лишити порожньою)"
            fullWidth
            value={renameTitle}
            onChange={(e) => setRenameTitle(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenameOpen(false)}>Скасувати</Button>
          <Button variant="contained" onClick={saveRename}>
            Зберегти
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
